import {
  DataStatus,
  DropdownGroup,
  DropdownItem,
  FormControl,
  FormField,
  LabelActions,
  openDropdown,
  showNotification,
} from 'platform/components';
import {match} from 'ts-pattern';

import {useState} from 'react';
import {UseFormReturn, useWatch} from 'react-hook-form';

import {always, isNil, isNotNil} from 'ramda';

import {
  ContextTemplateResponseBody,
  TemplateContextEnum,
  useGetContextTemplateListQuery,
  useRenderBusinessCaseSmsTemplateMutation,
  useRenderCustomerSmsTemplateMutation,
  useRenderInterestSmsTemplateMutation,
  useRenderRentalOrderSmsTemplateMutation,
  useRenderServiceOrderSmsTemplateMutation,
  useRenderVehicleSmsTemplateMutation,
  useRenderWorkshopInterestSmsTemplateMutation,
} from '@dms/api/core';
import {EntityResourceIds} from '@dms/api/shared';
import i18n from '@dms/i18n';

import {Nullish, suffixTestId, TestIdProps} from 'shared/web';

import {useTemplatesByLocale} from '../../../hooks/useTemplatesByLocale';
import {useTenant} from '../../../hooks/useTenant';
import {handleApiError} from '../../../utils/handleApiError';
import {replaceVariables, VARIABLE_REGEX} from '../../../utils/replaceVariables';
import {AiComposerEditor} from '../../AiMessageComposer/AiComposerDraft';
import {AiMessageComposerPanel} from '../../AiMessageComposer/AiMessageComposerPanel';
import {AiComposerAppliedStatus} from '../../AiMessageComposer/components/AiComposerAppliedStatus';
import {AiComposerSelectionActions} from '../../AiMessageComposer/components/AiComposerSelectionActions';
import {getAiComposerEditorHighlights} from '../../AiMessageComposer/getAiComposerEditorHighlights';
import {useAiMessageComposer} from '../../AiMessageComposer/useAiMessageComposer';
import {FormValues} from '../types/FormValues';

interface MessageFormFieldProps extends TestIdProps {
  formApi: UseFormReturn<FormValues>;
  control: FormControl<FormValues>;
  resourceId: EntityResourceIds | Nullish;
  recordId: string | Nullish;
  preferredLocale?: string | Nullish;

  service?: {
    serviceOrderId: string | Nullish;
    serviceCaseId: string | Nullish;
  };
}

export function MessageFormField(props: MessageFormFieldProps) {
  if (props.resourceId === EntityResourceIds.directSale) {
    return (
      <FormField
        control={props.control}
        name="body"
        type="textarea"
        label={i18n.t('general.labels.message')}
        isRequired
        isScrollable
        rows={7}
        data-testid={suffixTestId('messageFormField', props)}
      />
    );
  }
  return <AdvancedMessageFormField {...props} />;
}

function AdvancedMessageFormField(props: MessageFormFieldProps) {
  const {tenant} = useTenant();

  const context = match<EntityResourceIds | Nullish, TemplateContextEnum | null>(props.resourceId)
    .with(EntityResourceIds.businessCase, always('business-case'))
    .with(EntityResourceIds.serviceOrder, always('service-order'))
    .with(EntityResourceIds.customer, always('customer'))
    .with(EntityResourceIds.rentalOrder, always('rental-order'))
    .with(EntityResourceIds.vehicle, always('vehicle'))
    .with(EntityResourceIds.interest, always('interest'))
    .with(EntityResourceIds.workshopInterest, always('workshop-interest'))
    .otherwise(always(null));

  const hasContext = isNotNil(context);

  const [renderCustomerTemplate, {isLoading: isRenderCustomerTemplateInProgress}] =
    useRenderCustomerSmsTemplateMutation();
  const [renderBusinessCaseTemplate, {isLoading: isRenderBusinessCaseTemplateInProgress}] =
    useRenderBusinessCaseSmsTemplateMutation();
  const [renderServiceOrderTemplate, {isLoading: isRenderServiceOrderTemplateInProgress}] =
    useRenderServiceOrderSmsTemplateMutation();
  const [renderRentalOrderTemplate, {isLoading: isRenderRentalOrderTemplateInProgress}] =
    useRenderRentalOrderSmsTemplateMutation();
  const [renderVehicleTemplate, {isLoading: isRenderVehicleTemplateInProgress}] =
    useRenderVehicleSmsTemplateMutation();
  const [renderInterestTemplate, {isLoading: isRenderInterestTemplateInProgress}] =
    useRenderInterestSmsTemplateMutation();
  const [renderWorkshopInterestTemplate, {isLoading: isRenderWorkshopInterestTemplateInProgress}] =
    useRenderWorkshopInterestSmsTemplateMutation();

  const isRenderTemplateInProgress =
    isRenderCustomerTemplateInProgress ||
    isRenderBusinessCaseTemplateInProgress ||
    isRenderServiceOrderTemplateInProgress ||
    isRenderRentalOrderTemplateInProgress ||
    isRenderVehicleTemplateInProgress ||
    isRenderInterestTemplateInProgress ||
    isRenderWorkshopInterestTemplateInProgress;

  const {
    data: templates,
    isLoading: isLoadingTemplates,
    isError: isTemplatesError,
  } = useGetContextTemplateListQuery(
    {
      type: 'sms',
      context: context ?? 'customer',
    },
    {skip: !hasContext}
  );

  const getRenderer = (template: ContextTemplateResponseBody) => {
    if (props.resourceId === EntityResourceIds.rentalOrder && isNotNil(props.recordId)) {
      return renderRentalOrderTemplate({
        body: {
          rentalOrderId: props.recordId,
          template: template.body,
        },
      });
    }

    if (props.resourceId === EntityResourceIds.vehicle && isNotNil(props.recordId)) {
      return renderVehicleTemplate({
        body: {
          vehicleId: props.recordId,
          template: template.body,
        },
      });
    }

    if (props.resourceId === EntityResourceIds.businessCase && isNotNil(props.recordId)) {
      return renderBusinessCaseTemplate({
        renderBusinessCaseSmsTemplateRequestBody: {
          template: template.body,
          businessCaseId: props.recordId,
        },
      });
    }

    if (props.resourceId === EntityResourceIds.interest && isNotNil(props.recordId)) {
      const locale = template.locale ?? tenant?.defaultLocale;
      if (isNil(locale)) {
        showNotification.error(i18n.t('general.notifications.dataStillLoadingTryAgain'));
        return;
      }
      return renderInterestTemplate({
        body: {
          template: template.body,
          interestId: props.recordId,
          locale,
        },
      });
    }

    if (props.resourceId === EntityResourceIds.workshopInterest && isNotNil(props.recordId)) {
      const locale = template.locale ?? tenant?.defaultLocale;
      if (isNil(locale)) {
        showNotification.error(i18n.t('general.notifications.dataStillLoadingTryAgain'));
        return;
      }
      return renderWorkshopInterestTemplate({
        body: {
          template: template.body,
          workshopInterestId: props.recordId,
          locale,
        },
      });
    }

    if (isNotNil(props.service?.serviceCaseId) && isNotNil(props.service?.serviceOrderId)) {
      return renderServiceOrderTemplate({
        renderServiceOrderSmsTemplateRequestBody: {
          template: template.body,
          serviceCaseId: props.service.serviceCaseId,
          serviceOrderId: props.service.serviceOrderId,
        },
      });
    }

    if (isNotNil(props.recordId)) {
      return renderCustomerTemplate({
        renderCustomerSmsTemplateRequestBody: {
          template: template.body,
          customerId: props.recordId,
        },
      });
    }
  };

  const onTemplateClick = (template: ContextTemplateResponseBody) => {
    const renderer = getRenderer(template);

    if (!renderer) {
      return;
    }

    return renderer
      .unwrap()
      .then((response) => {
        props.formApi.setValue('body', replaceVariables(response.body));
        if (VARIABLE_REGEX.test(response.body)) {
          showNotification.warning(i18n.t('general.notifications.notAllPlaceholdersFilled'));
        }
      })
      .catch(handleApiError);
  };

  const templatesByLocale = useTemplatesByLocale(templates, props.preferredLocale);

  const [body, selectedCustomerId, recipientPhoneNumber] = useWatch({
    control: props.control,
    name: ['body', 'customerId', 'phoneNumber'],
  });

  const [editor, setEditor] = useState<AiComposerEditor>(null);

  const composer = useAiMessageComposer({
    channel: 'sms',
    bodyFormat: 'PLAIN_TEXT',
    draft: {body: body ?? ''},
    // SMS has no source record; the recipient still belongs to the identity, so a proposal does
    // not survive a change of customer or phone number.
    record: {
      context: null,
      recordId: null,
      customerId: selectedCustomerId,
      recipientAddress: recipientPhoneNumber,
    },
    editor,
    locale: props.preferredLocale,
    onApply: (draft) => props.formApi.setValue('body', draft.body),
  });

  const templateAction: LabelActions[number] = {
    title: i18n.t('entity.customer.labels.chooseSmsTemplate'),
    leftIcon: 'action/autorenew',
    isDisabled: isRenderTemplateInProgress,
    onClick: (event) =>
      openDropdown(
        event,
        <>
          <DataStatus isError={isTemplatesError} isLoading={isLoadingTemplates} />
          {templatesByLocale.isGrouped
            ? templatesByLocale.groups.map((group) => (
                <DropdownGroup key={group.key} label={group.label}>
                  {group.templates.map((template) => (
                    <DropdownItem
                      label={template.name}
                      key={template.id}
                      onClick={() => onTemplateClick(template)}
                    />
                  ))}
                </DropdownGroup>
              ))
            : templatesByLocale.templates.map((template) => (
                <DropdownItem
                  label={template.name}
                  key={template.id}
                  onClick={() => onTemplateClick(template)}
                />
              ))}
        </>,
        {
          isHeightLimited: true,
        }
      ),
  };

  const labelActions: LabelActions = [
    ...(isNotNil(composer.labelAction) ? [composer.labelAction] : []),
    ...(hasContext ? [templateAction] : []),
  ];

  return (
    <FormField
      control={props.control}
      name="body"
      type="textarea"
      label={i18n.t('general.labels.message')}
      labelActions={labelActions}
      onEditorReady={setEditor}
      selectionContent={
        composer.isEnabled ? (
          <AiComposerSelectionActions
            controller={composer}
            data-testid={suffixTestId('messageFormField', props)}
          />
        ) : undefined
      }
      editorHighlights={getAiComposerEditorHighlights(composer)}
      onKeyDownCapture={composer.onEditorKeyDownCapture}
      footerContent={
        <AiComposerAppliedStatus
          controller={composer}
          data-testid={suffixTestId('messageFormField', props)}
        />
      }
      editorContent={
        <AiMessageComposerPanel
          controller={composer}
          data-testid={suffixTestId('messageFormField', props)}
        />
      }
      isRequired
      isScrollable
      rows={7}
      data-testid={suffixTestId('messageFormField', props)}
    />
  );
}
